<template>
	<HeroLayout v-model="showContactUs">
		<div class="flex flex-col h-full w-full">
			<img :src="logoLargePath" alt="Logo" class="registration-title-image" />
			<p class="registration-title whitespace-pre-wrap">{{ login_screen_message }}</p>
			<p v-if="false" class="registration-subtitle mt-1 mb-10">Don't have an account?
				<router-link :to="{ name: 'auth.register' }" class="clickable-text">Sign up</router-link>
				.
			</p>
			<div v-else class="mt-1 mb-10"></div>
			<form>
				<InputText label="Email address*" type="email" autocomplete="email" placeholder="Enter your email address"
								v-model="email" :errors="error" error-field="user.email" class="userpilot-mask" />

				<div class="flex relative mt-8">
					<InputText label="Password*" :type="hidePassword ? 'password' : 'text'" autocomplete="current-password" class="w-full userpilot-mask"
									placeholder="Enter your password" v-model="password"
									:icon="hidePassword ? InputIcon.PASSWORD_LOCKED : InputIcon.PASSWORD_UNLOCKED" :errors="error" error-field="user.password" />
					<p class="registration-password-toggle" v-if="hidePassword" @click.prevent="hidePassword = false">Show password</p>
					<p class="registration-password-toggle" v-if="!hidePassword" @click.prevent="hidePassword = true">Hide password</p>
				</div>

				<div class="w-full text-right">
					<router-link :to="{ name: 'auth.forgot-password' }" class="clickable-text">Forgot password</router-link>
				</div>

				<SubmitButton class="ml-auto mt-8" label="Log In" :loading="isLoading" @click="login"  />
				<ValidationError :errors="error" :field="null" />
			</form>
		</div>
	</HeroLayout>
</template>

<script setup lang="js">
import { onMounted, ref } from 'vue'
import InputText from '@/components/Forms/InputText.vue'
import { useAuthStore } from '@/stores/authStore.js'
import ValidationError from '@/components/Forms/InputError.vue'
import HeroLayout from '@/layouts/HeroLayout.vue'
import router from '@/router/router.js'
import { userApi } from '@/api/User/UserApi.js'
import { useSignupStore } from '@/stores/signupStore.js'
import { InputIcon } from '@/models/enums/InputIcon.js'
import SubmitButton from '@/components/Forms/Buttons/SubmitButton.vue'
import { Tenants } from '@/models/tenants/Tenants.js'
import { Toaster, Banner } from '@/models/utils/Toaster.js';

const isLoading = ref(false)
const hidePassword = ref(true)
const error = ref(null)
const authStore = useAuthStore()
const signupStore = useSignupStore()
const email = ref(import.meta.env.VITE_DEBUG_USER_NAME)
const password = ref(import.meta.env.VITE_DEBUG_PASSWORD)
const showContactUs = ref(false)
const tenant = ref(null)
const logoLargePath = ref('')
const login_screen_message = ref('')
const toaster = new Toaster()
const banner = new Banner()

onMounted(() =>
{
	toaster.clear()
	authStore.logout(false)
	let toastMessage = signupStore.getLoginToastMessage

	if (toastMessage)
	{
		toaster.success(toastMessage)

		signupStore.setLoginToastMessage(null)
	}

	// Landed here because another tab logged out (see useAuthSync). The message rides in on the query
	// rather than signupStore.loginToastMessage because that key is shared across tabs and is cleared
	// on read, so the tab that deliberately logged out would race this one for it.
	if (router.currentRoute.value.query.signedOut)
	{
		banner.info('You were signed out in another tab.')

		router.replace({ name: 'auth.login' })
	}

	tenant.value = Tenants.getTenant()
	logoLargePath.value = new URL(`../../assets/images/theme/${tenant.value.registrationLogo.image}`, import.meta.url).href

	loadTenantSettings()
})

function loadTenantSettings() {
	userApi.getTenantLoginMessage(tenant.value.id, (tenantData) => {
		login_screen_message.value = tenantData.data.message
	})
}

function login()
{
	isLoading.value = true;

	userApi.login(email.value, password.value, tenant.value.id,
		(shouldNavigate) =>
		{
			isLoading.value = false
			if (shouldNavigate)
			{
				const user = authStore.getAuth?.user()
				if (!user?.given_name || !user?.family_name || !user?.phone_number)
				{
					router.push({ name: 'auth.add-profile-info' })
				}
				else
				{
					authStore.userpilotIdentify()
					router.push({ name: 'dashboard' })
				}
			}
		}, (e) =>
		{
			isLoading.value = false
			error.value = e.showErrorBanner()
		})
}
</script>